import { DEFAULT_BRANDING, setBranding } from '../../redux/slices/branding/brandingSlice';
import { applyBrandingCss } from './applyBrandingCss';
import { clearCachedBranding, getCachedBranding, setCachedBranding } from './brandingCache';

/**
 * One place for the signed-in account's white-label branding, so every login path (email,
 * Google), the branding refresh and logout agree: the brand is applied to the CSS variables,
 * kept in Redux and cached for the next page load, or reset to the Bridged default.
 */

/** Builds the branding state from a team / brand payload (`/User/Login` team or `/team/brand`). */
export const brandingFromTeam = (team, extras = {}) => {
  if (!team?.isWhitelabelingEnabled) {
    return { ...DEFAULT_BRANDING, ...extras };
  }

  return {
    ...DEFAULT_BRANDING,
    logoUrl: team.logo || DEFAULT_BRANDING.logoUrl,
    primaryColor: team.primaryColor || DEFAULT_BRANDING.primaryColor,
    brandingEnabled: true,
    ...extras,
  };
};

/** Applies a branding to the page, Redux and the cache. */
export const applySessionBranding = (dispatch, branding) => {
  applyBrandingCss(branding.primaryColor || DEFAULT_BRANDING.primaryColor);
  dispatch?.(setBranding(branding));
  setCachedBranding(branding);
};

/** Back to the Bridged default, with nothing cached. */
export const resetSessionBranding = dispatch => {
  applyBrandingCss(DEFAULT_BRANDING.primaryColor);
  dispatch?.(setBranding(DEFAULT_BRANDING));
  clearCachedBranding();
};

/**
 * On logout: keep the white-label look (logo, colour, name) so the login page stays on brand
 * for this team, but drop the account's settings (sponsors access, visualization config) so
 * they never carry over. The next login replaces it with that account's branding, or the
 * default when the account isn't white-labelled. Without white-labelling, nothing is kept.
 */
export const keepBrandingForLoginPage = () => {
  const cached = getCachedBranding();

  if (!cached?.brandingEnabled) {
    resetSessionBranding();
    return;
  }

  const loginBranding = {
    ...DEFAULT_BRANDING,
    logoUrl: cached.logoUrl || DEFAULT_BRANDING.logoUrl,
    primaryColor: cached.primaryColor || DEFAULT_BRANDING.primaryColor,
    companyName: cached.companyName || DEFAULT_BRANDING.companyName,
    brandingEnabled: true,
  };
  applyBrandingCss(loginBranding.primaryColor);
  setCachedBranding(loginBranding);
};
